import CTNButton from '@ctn/ui/components/ctn-button';
import { CTNIcon } from '@ctn/ui/components/ctn-icon';
import CTNSpinner from '@ctn/ui/components/ctn-spinner';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePreviousProp } from '@/hooks/usePreviousProp';

import { ROUTES } from '@/constants/routes.constants';
import {
	SOLICITUD_ESTADOS,
} from '@/constants/tramite.constants';

const classNameComponent = 'c-pnp-overview';

export type Data = { list: Array<{ idState: number; description: string; counter: number }>; total: number };

type SolicitudesOverviewProps = {
	readonly className?: string;
	readonly data?: Data;
	readonly isLoading?: boolean;
};

const ESTADO_COLOR = {
	[SOLICITUD_ESTADOS.BORRADOR]: { color: 'gray-70', bg: 'gray-20' },
	[SOLICITUD_ESTADOS.ENVIADA]: { color: 'orange-50', bg: 'orange-10' },
	[SOLICITUD_ESTADOS.EN_CURSO]: { color: 'blue-50', bg: 'blue-10' },
	[SOLICITUD_ESTADOS.FINALIZADA]: { color: 'green-50', bg: 'green-10' },
	[SOLICITUD_ESTADOS.CANCELADA]: { color: 'red-50', bg: 'red-10' },
};

const SolicitudesOverview = ({ className, data, isLoading = false }: SolicitudesOverviewProps) => {
	const navigate = useNavigate();
	const classNameSolicitudesOverview = useMemo(() => classNames(className, classNameComponent), [className]);
	const makeParams = (idState: number) => new URLSearchParams({ idEstado: idState.toString() });
	const prevIsLoading = usePreviousProp(isLoading);
	const [isLoaded, setIsLoaded] = useState<boolean>(false);

	useEffect(() => {
		if (!isLoading && Boolean(data)) {
			setIsLoaded((prevState) => (prevIsLoading && !isLoading) ? prevIsLoading : prevState);
		}
	}, [isLoading]);

	return (
		<section className={classNameSolicitudesOverview}>
			<header className="o-splitter">
				<h3 className="c-pnp-heading-h4 o-media">
					{(isLoaded && data) && data.total > 0 && (
						<span className={`o-media__img ${classNameComponent}__total`}>{data.total}</span>
					)}
					<span className="o-media__body">
						Solicitudes
					</span>
				</h3>
				{(isLoaded && data) && data.total > 0 && (
					<CTNButton
						label="Ver todas"
						disabled={!data?.list}
						className={`${classNameComponent}__btn`}
						mode="secondary"
						size="size-32"
						variant="ghost"
						onClick={() => navigate(ROUTES.SOLICITUDES)}
					/>
				)}
			</header>
			{(!isLoaded) && (
				<div className={`${classNameComponent}__loading`}>
					<CTNSpinner size="size-40" />
					<p>Cargando datos</p>
				</div>
			)}
			{(isLoaded && !data?.total) && (
				<ul className={`${classNameComponent}__list`}>
					<li>
						<span className="o-media">
							<CTNIcon icon="check" color="blue-50" className="o-media__img" />
							<span className="o-media__body">
								Gestiona las solicitudes que hayas iniciado en este portal.
							</span>
						</span>
					</li>
					<li>
						<span className="o-media">
							<CTNIcon icon="check" color="blue-50" className="o-media__img" />
							<span className="o-media__body">Manténte informado de su estado. </span>
						</span>
					</li>
					<li>
						<span className="o-media">
							<CTNIcon icon="check" color="blue-50" className="o-media__img" />
							<span className="o-media__body">Realiza los pasos necesarios hasta su firma.</span>
						</span>
					</li>
					<li>
						<span className="o-media">
							<CTNIcon icon="check" color="blue-50" className="o-media__img" />
							<span className="o-media__body">
								Dispón de un repositorio de las solicitudes ya firmadas.
							</span>
						</span>
					</li>
				</ul>
			)}
			{(isLoaded && (data?.total ?? 0) > 0 ) && (
				<table className={`[ ${classNameComponent}__table ] [ u-mt-16 ]`}>
					<tbody>
						{data?.list?.map(({ idState, description, counter }) => (
							<tr key={idState} className={`${classNameComponent}__tr`}>
								<td className={`${classNameComponent}__td`}>
									<span
										className={`${classNameComponent}__badge`}
										style={{
											[`--${classNameComponent}__badge--Color` as string]: `var(--${ESTADO_COLOR[idState]?.color ?? 'gray-70'})`,
											[`--${classNameComponent}__badge--BackgroundColor` as string]: `var(--${ESTADO_COLOR[idState]?.bg ?? 'gray-20'})`,
										}}
									>
										{counter}
									</span>
								</td>
								<td className={`${classNameComponent}__td ${classNameComponent}__td--desc`}>
									{description}
								</td>
								<td className={`${classNameComponent}__td`}>
									<CTNButton
										label="Ver"
										mode="secondary"
										className={`${classNameComponent}__btn`}
										size="size-32"
										variant="ghost"
										onClick={() => {
											navigate(`${ROUTES.SOLICITUDES}?${makeParams(idState).toString()}`);
										}}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</section>
	);
};

export default SolicitudesOverview;
